import React, { useEffect, useState } from "react";
import { X, UserPlus, Trash2, Crown, User as UserIcon, Clock } from "lucide-react";
import clsx from "clsx";
import { toast } from "sonner";

import { membersApi } from "@/lib/api";
import { confirm } from "@/lib/dialog";

/**
 * Project share dialog. Lists members + lets the owner invite new ones by email
 * and change roles. Invitees who haven't signed in yet get a "pending" badge —
 * their access activates automatically the first time they sign in with that
 * Google email.
 */
export default function ShareDialog({ open, onClose, project, myRole, myUserId }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [busy, setBusy] = useState(false);

  const isOwner = myRole === "owner";

  useEffect(() => {
    if (!open || !project) return;
    setLoading(true);
    membersApi
      .list(project.id)
      .then(setMembers)
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load members"))
      .finally(() => setLoading(false));
  }, [open, project]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, busy]);

  if (!open || !project) return null;

  const invite = async () => {
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail || !/.+@.+\..+/.test(cleanEmail)) {
      toast.error("Valid email required");
      return;
    }
    setBusy(true);
    try {
      const m = await membersApi.invite(project.id, { email: cleanEmail, role });
      setMembers((prev) => {
        // Replace existing membership row for the same email, or append.
        const others = prev.filter((p) => p.email !== m.email || p.deleted_at);
        return [...others, m];
      });
      setEmail("");
      toast.success(m.status === "active" ? "Member added" : "Invite sent — pending first sign-in");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invite failed");
    } finally {
      setBusy(false);
    }
  };

  const changeRole = async (m, newRole) => {
    setBusy(true);
    try {
      const updated = await membersApi.update(project.id, m.id, { role: newRole });
      setMembers((prev) => prev.map((p) => (p.id === m.id ? updated : p)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to change role");
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async (m) => {
    const isSelf = m.user_id === myUserId;
    const ok = await confirm({
      title: isSelf ? `Leave "${project.name}"?` : `Remove ${m.email}?`,
      body: isSelf ? "You'll lose access to this project." : "They'll lose access to this project.",
      confirmLabel: isSelf ? "LEAVE" : "REMOVE",
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await membersApi.remove(project.id, m.id);
      setMembers((prev) => prev.filter((p) => p.id !== m.id));
      toast.success(isSelf ? "Left project" : "Removed");
      if (isSelf) onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
    } finally {
      setBusy(false);
    }
  };

  const live = members.filter((m) => !m.deleted_at);

  return (
    <div
      className="fixed inset-0 z-[85] flex items-center justify-center bg-black/70 backdrop-blur-sm p-6"
      onClick={busy ? undefined : onClose}
      data-testid="share-overlay"
    >
      <div
        className="w-[600px] max-w-full bg-bg-surface border border-line-strong shadow-[0_24px_64px_rgba(0,0,0,0.8)] animate-slide-up max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="share-dialog"
      >
        <header className="flex items-center justify-between p-3 border-b border-line-strong bg-bg-elevated">
          <div>
            <div className="mono text-2xs uppercase tracking-[0.2em] text-ink-mute">SHARE</div>
            <div className="text-sm text-ink mt-0.5">{project.name}</div>
          </div>
          <button onClick={onClose} disabled={busy} className="btn-ghost" data-testid="share-close">
            <X className="w-3 h-3" />
          </button>
        </header>

        {isOwner && (
          <div className="p-3 border-b border-line bg-bg-base flex items-center gap-2">
            <UserPlus className="w-3 h-3 text-ink-mute shrink-0" />
            <input
              type="email"
              placeholder="email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") invite(); }}
              className="flex-1 bg-bg-elevated text-sm p-1.5 outline-none border border-line focus:border-amber"
              data-testid="share-invite-email"
            />
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="mono text-2xs uppercase tracking-widest bg-bg-elevated text-ink p-1.5 outline-none border border-line focus:border-amber"
              data-testid="share-invite-role"
            >
              <option value="member">MEMBER</option>
              <option value="owner">OWNER</option>
            </select>
            <button
              type="button"
              onClick={invite}
              disabled={busy || !email.trim()}
              className="btn-primary px-3"
              data-testid="share-invite-btn"
            >
              INVITE
            </button>
          </div>
        )}

        <div className="flex-1 overflow-auto p-3 space-y-1.5">
          {loading ? (
            <div className="mono text-2xs uppercase tracking-widest text-ink-mute text-center py-8">
              loading members…
            </div>
          ) : live.length === 0 ? (
            <div className="mono text-2xs uppercase tracking-widest text-ink-mute text-center py-8">
              no members
            </div>
          ) : (
            live.map((m) => {
              const isSelf = m.user_id === myUserId;
              const isPending = m.status === "pending";
              return (
                <div
                  key={m.id}
                  className="flex items-center gap-2 p-2 border border-line bg-bg-elevated"
                  data-testid={"share-row-" + m.id}
                >
                  {m.role === "owner" ? (
                    <Crown className="w-3.5 h-3.5 text-amber shrink-0" />
                  ) : (
                    <UserIcon className="w-3.5 h-3.5 text-ink-mute shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-ink truncate">
                      {m.email} {isSelf && <span className="text-2xs text-ink-mute">(you)</span>}
                    </div>
                    <div className="mono text-2xs uppercase tracking-widest text-ink-mute mt-0.5 flex items-center gap-2">
                      {isPending ? (
                        <span className="text-amber flex items-center gap-1">
                          <Clock className="w-2.5 h-2.5" /> PENDING SIGN-IN
                        </span>
                      ) : (
                        <span>ACTIVE</span>
                      )}
                    </div>
                  </div>
                  {isOwner && !isSelf ? (
                    <select
                      value={m.role}
                      onChange={(e) => changeRole(m, e.target.value)}
                      disabled={busy}
                      className="mono text-2xs uppercase tracking-widest bg-bg-base text-ink p-1 outline-none border border-line focus:border-amber"
                      data-testid={"share-role-" + m.id}
                    >
                      <option value="member">MEMBER</option>
                      <option value="owner">OWNER</option>
                    </select>
                  ) : (
                    <span
                      className={clsx(
                        "mono text-2xs uppercase tracking-widest px-2 py-1 border",
                        m.role === "owner" ? "text-amber border-amber/40" : "text-ink-dim border-line"
                      )}
                    >
                      {m.role}
                    </span>
                  )}
                  {(isOwner || isSelf) && (
                    <button
                      type="button"
                      onClick={() => removeMember(m)}
                      disabled={busy}
                      className="text-ink-mute hover:text-red shrink-0"
                      title={isSelf ? "Leave project" : "Remove member"}
                      data-testid={"share-remove-" + m.id}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>

        <footer className="px-3 py-2 border-t border-line bg-bg-elevated mono text-2xs uppercase tracking-widest text-ink-mute">
          Pending invites activate automatically when that Google email signs in.
        </footer>
      </div>
    </div>
  );
}
